import React from 'react'
import { Select, Spin, Typography } from 'antd'
import { sellerApi } from 'api/seller'
import { ESellerStatus, ESellerTypes, ISeller } from 'models/seller'
import { showMessage } from 'shared/utils/showMessage'
import { getRequestErrorText } from 'components/chats/utils'
import styles from './SupportSellerPicker.module.scss'

const { Text } = Typography

interface ISupportSellerPickerProps {
  onSelect: (seller: ISeller) => void
}

// У юрлица название приходит из DaData уже с формой собственности
// («ИП …», «ООО …»), у физлица есть только владелец
const getSellerOwnerLine = (seller: ISeller) =>
  (seller.type === ESellerTypes.LEGAL && seller.company_info?.name) ||
  [seller.user_id?.name, seller.user_id?.surname].filter(Boolean).join(' ')

export const SupportSellerPicker: React.FC<ISupportSellerPickerProps> = ({ onSelect }) => {
  const [sellers, setSellers] = React.useState<ISeller[] | null>(null)
  const [isLoading, setIsLoading] = React.useState(false)

  // Список всех продавцов тяжёлый: грузим, только когда в поле нажали
  const loadSellers = async () => {
    if (sellers || isLoading) return

    setIsLoading(true)
    try {
      const { data } = await sellerApi.getSearchSellers()
      setSellers(data.filter((seller) => seller.status === ESellerStatus.APPROVED))
    } catch (e) {
      showMessage('error', 'Не удалось загрузить продавцов', getRequestErrorText(e))
    } finally {
      setIsLoading(false)
    }
  }

  const options = React.useMemo(
    () => (sellers || [])
      .map((seller) => {
        const name = seller.shop_details?.shop_name || 'Без названия'
        const owner = getSellerOwnerLine(seller)

        return {
          value: seller._id,
          name,
          searchText: `${name} ${owner}`.toLowerCase(),
          label: (
            <div className={styles.option}>
              <span className={styles.optionName}>{name}</span>
              {owner && <span className={styles.optionOwner}>{owner}</span>}
            </div>
          )
        }
      })
      .sort((a, b) => a.name.localeCompare(b.name, 'ru')),
    [sellers]
  )

  const handleSelect = (sellerId: string) => {
    const seller = sellers?.find((item) => item._id === sellerId)
    if (seller) onSelect(seller)
  }

  return (
    <div className={styles.picker}>
      <Text type='secondary' className={styles.hint}>или напишите селлеру</Text>
      <Select<string, (typeof options)[number]>
        showSearch
        value={null}
        className={styles.select}
        placeholder='Магазин, ИП или имя'
        options={options}
        filterOption={(input, option) => Boolean(option?.searchText.includes(input.trim().toLowerCase()))}
        onFocus={loadSellers}
        onSelect={handleSelect}
        loading={isLoading}
        notFoundContent={isLoading ? <Spin size='small' /> : 'Никого не нашли'}
      />
    </div>
  )
}
